/**
 * Uploading files or whole folders into the file tree: first a list of what will go up and where,
 * then, once confirmed, one file at a time, showing each one's progress.
 */
import { Check, CircleX, File as FileIcon, FileText } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { formatBytes } from "../admin/data";
import { Field, Modal } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import "./upload.css";

/** A file to upload, with where it sits in what was picked ("photos/a.jpg" when the folder "photos" was picked). */
export interface PickedFile {
  file: File;
  path: string;
}

export interface Picked {
  files: PickedFile[];
  /** Paths left out as clutter. */
  skipped: string[];
}

// What computers leave in folders. The server refuses anything in .git anyway.
const JUNK = /^(\.git|__MACOSX|\.DS_Store|Thumbs\.db|desktop\.ini|\._.+)$/i;

/** The part of `path` that makes it clutter (".git" for "thesis/.git/HEAD"), or undefined. */
const junkPart = (path: string) => path.split("/").find((p) => JUNK.test(p));

function split(all: PickedFile[], skipped: string[] = []): Picked {
  const files: PickedFile[] = [];
  for (const p of all) {
    if (junkPart(p.path)) skipped.push(p.path);
    else files.push(p);
  }
  files.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  return { files, skipped };
}

/** Files from a file input. A folder input (webkitdirectory) gives each one's path inside the folder. */
export function pickedFromInput(list: FileList): Picked {
  return split(Array.from(list, (file) => ({ file, path: file.webkitRelativePath || file.name })));
}

/** Files dropped from the computer, walking into any folders. Call it inside the drop handler itself. */
export function pickedFromDrop(dt: DataTransfer): Promise<Picked> {
  // Taken now: the browser empties dataTransfer once the drop handler returns.
  const entries = Array.from(dt.items, (item) => item.webkitGetAsEntry()).filter((e): e is FileSystemEntry => e !== null);
  if (!entries.length) return Promise.resolve(split(Array.from(dt.files, (file) => ({ file, path: file.name }))));
  const files: PickedFile[] = [];
  const skipped: string[] = [];
  return Promise.all(entries.map((e) => walk(e, "", files, skipped))).then(() => split(files, skipped));
}

async function walk(entry: FileSystemEntry, parent: string, files: PickedFile[], skipped: string[]): Promise<void> {
  const path = parent ? `${parent}/${entry.name}` : entry.name;
  if (entry.isFile) {
    files.push({ path, file: await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject)) });
    return;
  }
  if (!entry.isDirectory) return;
  // Don't read through a .git folder's thousands of files just to leave them out.
  if (JUNK.test(entry.name)) {
    skipped.push(path);
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const children: FileSystemEntry[] = [];
  // It hands them over in batches (100 at a time in Chrome) until a batch comes back empty.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    children.push(...batch);
  }
  await Promise.all(children.map((c) => walk(c, path, files, skipped)));
}

type Status =
  | { state: "waiting" }
  | { state: "sending"; sent: number }
  | { state: "done" }
  | { state: "failed"; error: string }
  | { state: "stopped" };

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

interface Props {
  projectId: string;
  picked: Picked;
  /** Where it was dropped or picked for ("" for the top level). It can be changed before uploading. */
  folder: string;
  /** The project's folders, as other places to upload into. */
  folders: string[];
  /** Paths of the project's files, to mark the uploads that will replace one. */
  existing: Set<string>;
  /** When the upload starts, with the folder it's going into. */
  onStart: (folder: string) => void;
  onFinished: () => void;
  onClose: () => void;
}

export function UploadDialog({ projectId, picked, folder, folders, existing, onStart, onFinished, onClose }: Props) {
  const { files } = picked;
  const [dest, setDest] = useState(folder);
  const [phase, setPhase] = useState<"review" | "uploading" | "finished">("review");
  const [status, setStatus] = useState<Status[]>(() => files.map(() => ({ state: "waiting" })));
  const stop = useRef<AbortController | null>(null);
  const list = useRef<HTMLDivElement>(null);

  const target = (p: PickedFile) => (dest ? `${dest}/${p.path}` : p.path);
  const total = useMemo(() => files.reduce((n, p) => n + p.file.size, 0), [files]);
  const replacing = files.filter((p) => existing.has(target(p))).length;
  const skippedNames = [...new Set(picked.skipped.map(junkPart))].join(", ");
  // One folder picked or dropped on its own: say so, and where it will end up.
  const tops = new Set(files.map((p) => (p.path.includes("/") ? p.path.slice(0, p.path.indexOf("/")) : "")));
  const onlyFolder = tops.size === 1 && !tops.has("") ? [...tops][0] : null;

  const count = (state: Status["state"]) => status.filter((s) => s.state === state).length;
  const done = count("done");
  const failed = count("failed");
  const stopped = count("stopped");
  const sent = files.reduce((n, p, i) => {
    const s = status[i];
    return n + (s.state === "done" ? p.file.size : s.state === "sending" ? s.sent : 0);
  }, 0);
  const left = status.flatMap((s, i) => (s.state === "failed" || s.state === "stopped" ? [i] : []));

  async function upload(indices: number[]) {
    const ctrl = new AbortController();
    stop.current = ctrl;
    const set = (i: number, s: Status) => setStatus((all) => all.map((old, j) => (j === i ? s : old)));
    const chosen = new Set(indices);
    setStatus((all) => all.map((s, i) => (chosen.has(i) ? { state: "waiting" } : s)));
    setPhase("uploading");
    onStart(dest);
    for (const i of indices) {
      if (ctrl.signal.aborted) {
        set(i, { state: "stopped" });
        continue;
      }
      set(i, { state: "sending", sent: 0 });
      list.current?.children[i]?.scrollIntoView({ block: "nearest", behavior: "instant" });
      const url = `/api/projects/${projectId}/upload?path=${encodeURIComponent(target(files[i]))}`;
      try {
        await api.uploadWithProgress(url, files[i].file, (n) => set(i, { state: "sending", sent: n }), ctrl.signal);
        set(i, { state: "done" });
      } catch (e) {
        set(i, ctrl.signal.aborted ? { state: "stopped" } : { state: "failed", error: errorMessage(e) });
      }
    }
    stop.current = null;
    setPhase("finished");
    onFinished();
  }

  function close() {
    if (phase === "uploading") {
      const togo = count("waiting") + count("sending");
      if (!confirm(`Stop uploading? ${plural(togo, "file")} still ${togo === 1 ? "hasn't" : "haven't"} been uploaded.`)) return;
      stop.current?.abort();
    }
    onClose();
  }

  let footer;
  if (phase === "review") {
    footer = (
      <>
        <button key="cancel" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button key="go" className="btn btn-primary" autoFocus onClick={() => upload(files.map((_, i) => i))}>
          Upload {files.length === 1 ? "file" : plural(files.length, "file")}
        </button>
      </>
    );
  } else if (phase === "uploading") {
    footer = (
      <button key="stop" className="btn" onClick={() => stop.current?.abort()}>
        Stop
      </button>
    );
  } else {
    footer = (
      <>
        {left.length > 0 && (
          <button key="retry" className="btn" onClick={() => upload(left)}>
            {failed ? "Retry" : "Upload"} {plural(left.length, "file")}
          </button>
        )}
        <button key="done" className="btn btn-primary" autoFocus onClick={onClose}>
          Done
        </button>
      </>
    );
  }

  let summary;
  if (phase === "review") {
    summary = (
      <>
        <span>
          {plural(files.length, "file")} · {formatBytes(total)}
        </span>
        {replacing > 0 && <span className="chip chip-warn">{replacing === 1 ? "1 will replace a file already there" : `${replacing} will replace files already there`}</span>}
        {skippedNames && <span className="muted small">Leaving out {skippedNames}</span>}
      </>
    );
  } else if (phase === "uploading") {
    summary = (
      <>
        <span className="grow">
          Uploading {Math.min(done + failed + 1, files.length)} of {files.length}…
        </span>
        <span className="muted small">
          {formatBytes(sent)} of {formatBytes(total)}
        </span>
      </>
    );
  } else {
    summary = (
      <span className={failed ? "error-text" : undefined}>
        {done === files.length
          ? `Uploaded ${files.length === 1 ? "the file" : `all ${files.length} files`}.`
          : [`Uploaded ${done} of ${files.length}.`, failed && `${failed} failed.`, stopped && `${stopped} not uploaded.`].filter(Boolean).join(" ")}
      </span>
    );
  }

  return (
    <Modal title={onlyFolder ? `Upload folder “${onlyFolder}”` : files.length === 1 ? "Upload file" : `Upload ${files.length} files`} onClose={close} width={580} footer={footer}>
      <div className="stack">
        <Field label="Upload into" hint={onlyFolder ? `It goes in as ${dest ? `${dest}/` : ""}${onlyFolder}, keeping the folders inside it.` : undefined}>
          <select value={dest} disabled={phase !== "review"} onChange={(e) => setDest(e.target.value)}>
            <option value="">Top level of the project</option>
            {folders.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
        <div className="upload-summary">{summary}</div>
        {phase !== "review" && (
          <div className="upload-progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={sent}>
            <span style={{ width: `${total ? (sent / total) * 100 : 100}%` }} />
          </div>
        )}
        <div className="upload-list" ref={list}>
          {files.map((p, i) => (
            <Row key={p.path} item={p} status={status[i]} replaces={existing.has(target(p))} />
          ))}
        </div>
      </div>
    </Modal>
  );
}

const IMAGE = /\.(png|jpe?g|gif|svg|webp|bmp|avif)$/i;
const TEXT = /\.(tex|bib|cls|sty|bst|txt|md|csv|tsv|dat)$/i;

const Row = memo(function Row({ item, status, replaces }: { item: PickedFile; status: Status; replaces: boolean }) {
  const slash = item.path.lastIndexOf("/");
  let state;
  if (status.state === "waiting") state = formatBytes(item.file.size);
  else if (status.state === "sending") state = `${item.file.size ? Math.floor((status.sent / item.file.size) * 100) : 100}%`;
  else if (status.state === "done") state = <Check size={15} aria-label="Uploaded" />;
  else if (status.state === "failed") state = <CircleX size={15} aria-label="Failed" />;
  else state = "Not uploaded";
  return (
    <div className={`upload-row ${status.state}`}>
      <Thumb file={item.file} />
      <span className="upload-name" title={item.path}>
        {slash >= 0 && <span className="muted">{item.path.slice(0, slash + 1)}</span>}
        {item.path.slice(slash + 1)}
      </span>
      <span>{replaces && status.state === "waiting" && <span className="chip chip-warn">Replaces</span>}</span>
      <span className="upload-state">{state}</span>
      {status.state === "sending" && <span className="upload-bar" style={{ width: `${item.file.size ? (status.sent / item.file.size) * 100 : 100}%` }} />}
      {status.state === "failed" && <span className="upload-error">{status.error}</span>}
    </div>
  );
});

function Thumb({ file }: { file: File }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!IMAGE.test(file.name)) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <span className="upload-thumb">
      {src ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setSrc(null)} />
      ) : TEXT.test(file.name) ? (
        <FileText size={14} />
      ) : (
        <FileIcon size={14} />
      )}
    </span>
  );
}
